import React, { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Download, Share2, Copy, Check } from "lucide-react";

export default function ShareQRDialog({ open, onOpenChange, accountNumber, name }) {
  const canvasWrapRef = useRef(null);
  const [copied, setCopied] = React.useState(false);
  const payload = accountNumber ? `finvault://transfer?account=${accountNumber}` : "";

  const downloadPng = async () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    // Composite the QR on a branded card (1024×1280) for high-quality sharing.
    const out = document.createElement("canvas");
    out.width = 1024;
    out.height = 1280;
    const ctx = out.getContext("2d");
    // Background
    ctx.fillStyle = "#0E1A14";
    ctx.fillRect(0, 0, 1024, 1280);
    // Card panel
    ctx.fillStyle = "#15211A";
    roundRect(ctx, 64, 96, 896, 1088, 28);
    ctx.fill();
    // Header
    ctx.fillStyle = "#9DA39A";
    ctx.font = "600 22px Inter, Arial, sans-serif";
    ctx.fillText("FINVAULT", 96, 160);
    ctx.fillStyle = "#F0ECE0";
    ctx.font = "700 44px 'Work Sans', Arial, sans-serif";
    ctx.fillText("Pay me on FinVault", 96, 220);
    if (name) {
      ctx.fillStyle = "#9DA39A";
      ctx.font = "400 24px 'Work Sans', Arial, sans-serif";
      ctx.fillText(name, 96, 260);
    }
    // QR (white tile + black modules for max readability)
    const qrSize = 640;
    const qrX = (1024 - qrSize) / 2;
    const qrY = 340;
    ctx.fillStyle = "#FFFFFF";
    roundRect(ctx, qrX - 24, qrY - 24, qrSize + 48, qrSize + 48, 20);
    ctx.fill();
    ctx.drawImage(canvas, qrX, qrY, qrSize, qrSize);
    // Account number
    ctx.fillStyle = "#9DA39A";
    ctx.font = "600 18px Inter, Arial, sans-serif";
    ctx.fillText("ACCOUNT NUMBER", 96, qrY + qrSize + 100);
    ctx.fillStyle = "#F0ECE0";
    ctx.font = "700 56px ui-monospace, Menlo, Consolas, monospace";
    ctx.fillText(formatAccount(accountNumber), 96, qrY + qrSize + 160);
    // Footer
    ctx.fillStyle = "#9DA39A";
    ctx.font = "400 18px 'Work Sans', Arial, sans-serif";
    ctx.fillText("Scan with FinVault to send funds instantly.", 96, qrY + qrSize + 220);

    out.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finvault-${accountNumber}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Saved high-res image.");
    }, "image/png");
  };

  const shareNative = async () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas || !navigator.canShare) {
      toast.error("Native share unavailable. Download the image instead.");
      return;
    }
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `finvault-${accountNumber}.png`, { type: "image/png" });
      try {
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Pay me on FinVault",
            text: `My FinVault account number is ${accountNumber}. Scan the QR or paste this number into your Transfer page.`,
          });
        } else {
          await navigator.share({
            title: "Pay me on FinVault",
            text: `My FinVault account number is ${accountNumber}.`,
          });
        }
      } catch (e) { /* user cancelled */ }
    }, "image/png");
  };

  const copy = async () => {
    if (!accountNumber) return;
    try {
      await navigator.clipboard.writeText(accountNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Couldn't copy."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-qr-dialog">
        <DialogHeader>
          <DialogTitle>Share your account</DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-5">
          <div ref={canvasWrapRef} className="mx-auto bg-white rounded-2xl p-5 border border-[#D2CDBE] w-fit" data-testid="share-qr-canvas-wrap">
            <QRCodeCanvas
              value={payload}
              size={256}
              bgColor="#FFFFFF"
              fgColor="#0E1A14"
              level="M"
              includeMargin={false}
            />
          </div>
          <div className="text-center">
            <div className="text-eyebrow text-[#4F4D45]">Account number</div>
            <div className="font-mono text-2xl tracking-[0.22em] font-semibold mt-1" data-testid="share-qr-number">{formatAccount(accountNumber)}</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button onClick={downloadPng} className="bg-[#1B3526] hover:bg-[#0F2517]" data-testid="share-qr-download">
              <Download className="w-4 h-4 mr-1.5" /> Save
            </Button>
            <Button onClick={shareNative} variant="outline" className="border-[#D2CDBE]" data-testid="share-qr-share">
              <Share2 className="w-4 h-4 mr-1.5" /> Share
            </Button>
            <Button onClick={copy} variant="outline" className="border-[#D2CDBE]" data-testid="share-qr-copy">
              {copied ? <Check className="w-4 h-4 mr-1.5 text-[#264C39]" /> : <Copy className="w-4 h-4 mr-1.5" />} {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <p className="text-xs text-[#4F4D45] text-center leading-relaxed">
            Anyone with FinVault can scan or type your account number to send funds. Your wallet ID stays private.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatAccount(n) {
  if (!n) return "";
  const s = String(n);
  if (s.length !== 10) return s;
  return `${s.slice(0, 4)} ${s.slice(4, 7)} ${s.slice(7)}`;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
